import { useState } from 'react';
import clsx from '../utils/clsx';
import styles from '../styles/Battlefield.module.css';
import CoverFormula from './CoverFormula';
import ClimbFormula from './ClimbFormula';
import MapIcon from './MapIcon';

/**
 * What the pointer does, per mode.
 *
 * Kept here rather than in the toolbar's `title` attributes because a
 * tooltip you have to hunt for is not documentation - and of everything
 * in these two lists, only the wheel and Esc are guessable.
 */
const CONTROLS = {
  play: [
    ['Left-drag', 'measure - it magnets to a marker at either end, and targets whoever it lands on'],
    ['Click a marker', 'make them your target - a second click targets at once'],
    ['Click a marker, then click and hold', 'move them'],
    ['Wheel while moving them', 'raise or lower - they keep the height as they walk'],
    ['Alt while moving them', 'snap to the grid'],
    ['Right-drag', 'turn the map'],
    ['Middle-drag, or shift-drag', 'move the map'],
    ['Wheel', 'zoom'],
    ['Esc', 'dismiss the measurement'],
  ],
  build: [
    ['Left-drag', 'lay out the current shape'],
    ['Right-drag', 'erase, whichever tool is up'],
    ['Layers', 'how many cubes deep one drag builds'],
    ['Shift-right-drag', 'turn the map'],
    ['Middle-drag, or shift-drag', 'move the map'],
    ['Wheel', 'zoom'],
  ],
};

/** What a terrain kind is called, and what it means at the table. */
const TERRAIN_MEANING = {
  normal: ['Open ground', 'no effect'],
  difficult: ['Rough going', 'costs double to cross'],
  dangerous: ['Hazard', 'crossed at normal speed, at a price'],
  impassable: ['Impassable', 'cannot be entered'],
};

/**
 * The carousel's pages. Cover & shots are the rules the map is applying to
 * you - reachable once only from the GM's build bar, so no player had ever
 * seen how cover is worked out; Climbing is the other rule it applies
 * without being asked: what a climb takes out of a move, and that coming
 * down takes it too.
 */
const PAGES = [
  { key: 'controls', label: 'Controls' },
  { key: 'key', label: 'Key' },
  { key: 'rules', label: 'Cover & shots' },
  { key: 'climb', label: 'Climbing' },
];

function ControlsPage({ mode }) {
  return (
    <dl className={styles.legendList}>
      {(CONTROLS[mode] || CONTROLS.play).map(([gesture, meaning]) => (
        <div key={gesture} className={styles.legendRow}>
          <dt>{gesture}</dt>
          <dd>{meaning}</dd>
        </div>
      ))}
    </dl>
  );
}

function KeyPage({ materials, terrainKinds }) {
  const empty = materials.length + terrainKinds.length === 0;
  return (
    <dl className={styles.legendList} data-testid="legend-key">
      {empty && (
        <div className={styles.legendRow}>
          <dd>Nothing on the map yet.</dd>
        </div>
      )}
      {materials.map((material) => (
        <div key={material.key} className={styles.legendRow} data-testid={`legend-material-${material.key}`}>
          <dt>
            <span className={styles.legendSwatch} style={{ background: material.colour }} aria-hidden="true" />
            {material.name}
          </dt>
          <dd>{describeMaterial(material)}</dd>
        </div>
      ))}
      {terrainKinds.map((kind) => (
        <div key={kind} className={styles.legendRow} data-testid={`legend-terrain-${kind}`}>
          <dt>{TERRAIN_MEANING[kind]?.[0] ?? kind}</dt>
          <dd>{TERRAIN_MEANING[kind]?.[1]}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The page on show. */
function LegendPage({ page, mode, materials, terrainKinds, climb }) {
  if (page === 'controls') return <ControlsPage mode={mode} />;
  if (page === 'rules') return <CoverFormula />;
  if (page === 'climb') return <ClimbFormula {...(climb || {})} />;
  return <KeyPage materials={materials} terrainKinds={terrainKinds} />;
}

/** Previous, the page's name, next - round and round. */
function PageTurner({ label, onTurn }) {
  const n = PAGES.length;
  return (
    <div className={styles.legendHead}>
      <button
        type="button"
        className={styles.mode}
        data-testid="legend-prev"
        aria-label="Previous page"
        onClick={() => onTurn((p) => (p + n - 1) % n)}
      >
        ◀
      </button>
      <span data-testid="legend-page">{label}</span>
      <button
        type="button"
        className={styles.mode}
        data-testid="legend-next"
        aria-label="Next page"
        onClick={() => onTurn((p) => (p + 1) % n)}
      >
        ▶
      </button>
    </div>
  );
}

/**
 * BattlefieldLegend - what the pointer does, and what is on THIS map.
 *
 * A carousel of two pages rather than one long panel, because the two
 * questions are asked at different moments: "how do I do that" while
 * learning, "what is that grey block" while playing.
 *
 * The key lists only what is actually on the map in front of you, not
 * the whole catalogue. A legend that names eight materials when the map
 * has two is a legend nobody reads twice.
 */
export default function BattlefieldLegend(props) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const current = PAGES[page % PAGES.length];
  return (
    <div className={styles.legend} data-testid="battlefield-legend">
      <button
        type="button"
        className={clsx(styles.mode, open && styles.modeActive)}
        data-testid="legend-toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        title="What the controls do, and what is on this map"
      >
        <MapIcon name="legend" /> Legend
      </button>
      {open && (
        <div className={styles.legendBody} data-testid="legend-body">
          <PageTurner label={current.label} onTurn={setPage} />
          <LegendPage page={current.key} {...props} />
        </div>
      )}
    </div>
  );
}

// Maps, not objects: the values are numbers and must match strictly.
const SIGHT = new Map([
  [2, 'see through'],
  [1, 'translucent'],
]);
const PASSAGE = new Map([
  [2, 'walk through'],
  [1, 'slow going'],
]);

/**
 * A material in one line: what it does to sight, to movement, and to a
 * bullet. Three independent answers, which is the point of there being
 * three properties.
 */
function describeMaterial(material) {
  const sight = SIGHT.get(material.transparency) ?? 'blocks sight';
  const passage = PASSAGE.get(material.permeability) ?? 'solid';
  return `${sight} · ${passage} · hardness ${material.hardness}`;
}
